<template>
  <div id="vueApp" :class="siteSettingsStore.mainClass" :inert="isInert">
    <NotificationsComponent context="App" />
    <HeaderComponent :hide-titlebar="hideTitlebar" />
    <div class="grid-wrapper" :class="hideSidebar ? 'no-grid' : ''">
      <section class="main">
        <router-view />
        <FooterComponent class="no-print" :hide-footer="hideFooter" />
      </section>
      <SidebarComponent />
    </div>
    <AutoLogout :show-dialog="showAutoLogoutDialog" @close-modal="logoutUser" @save-modal="continueUserSession" />
  </div>

  <ChatBot v-if="showChatBot" />
</template>

<script setup lang="ts">
import { computed, ref, onBeforeMount, watch, onMounted, onUnmounted, nextTick } from "vue";
import { IRouteMetadata } from "@/routes";
import HeaderComponent from "@/components/shared/Header.vue";
import SidebarComponent from "@/components/shared/Sidebar.vue";
import FooterComponent from "@/components/shared/Footer.vue";
import { installPendo } from "@/common/pendo";
import { useUserStore } from "@/stores/user";
import AutoLogout from "@/components/shared/dialogs/AutoLogout/AutoLogout.vue";
import {
  AUTO_LOGOUT_INTERVAL_MSEC,
  MAX_SESSION_IDLE_DURATION,
  SESSION_CHECK_INTERVAL,
  PRELIMINARY_USER_KEEP_ALIVE_TIME_IN_MSEC,
  PRELIMINARY_USER_TOTAL_KEEP_ALIVE_TIME_IN_MINUTES,
  MSEC_IN_MINUTE,
  FLAGS,
} from "@/define";
import { logout } from "@/common/logout";
import { logEvent } from "@/common/logger";
import { useRoute } from "vue-router";
import { reloadSubscriberInfo } from "@/common/reloadSubscriberInfo";
import { useNotificationsStore } from "@/stores/notifications";
import NotificationsComponent from "@/components/shared/Notifications.vue";
import { unifiedApi } from "./common";
import { NotificationType } from "@/stores/notificationsInterfaces";
import { pageStates as buyFlowPageStates } from "@/components/Buy/BuyEnums";
import { changePageState } from "./components/Buy/BuyHelper";
import { useSiteSettingsStore } from "./stores/siteSettings";
import { layoutItemNames } from "./components/BuyEnhancedCart/DynamicComponents/Config";
import { useBuyFlowManagerStore } from "./stores/buyFlowManager";
import ChatBot from "./chatBot/components/chatBot.vue";

const componentName = "App";

logEvent("created", componentName);
const userStore = useUserStore();
const notificationsStore = useNotificationsStore();
const siteSettingsStore = useSiteSettingsStore();
const buyFlowManagerStore = useBuyFlowManagerStore();
const route = useRoute();
const showAutoLogoutDialog = ref<boolean>(false);
const hasChatBotBetaAccess = ref<boolean>(false);
const isInert = ref<boolean | undefined>(false);
const APP_ROOT_IDS = ["vueApp", "app"];
let inertObservers: MutationObserver[] = [];

function getAppRoots() {
  const roots: HTMLElement[] = [];
  for (const rootId of APP_ROOT_IDS) {
    const root = document.getElementById(rootId);
    if (root) {
      roots.push(root);
    }
  }

  return roots;
}

function clearInertState() {
  isInert.value = undefined;
  for (const root of getAppRoots()) {
    root.removeAttribute("inert");
  }
}

const hideSidebar = computed(() => {
  const metadata: IRouteMetadata | undefined = route.meta;
  return metadata?.hideSidebar;
});

const hideTitlebar = computed(() => {
  const metadata: IRouteMetadata | undefined = route.meta;
  return metadata?.hideTitlebar;
});

const hideFooter = computed(() => {
  const metadata: IRouteMetadata | undefined = route.meta;
  return metadata?.hideFooter;
});

const chatbotFlag = computed(() => FLAGS.ENABLE_CHAT_BOT === "true");

const showChatBot = computed(() => {
  return (chatbotFlag.value && userStore.currentUser) || hasChatBotBetaAccess.value;
});

onBeforeMount(() => {
  checkIfIsStillLogged();
});

onMounted(async () => {
  //Waits until the DOM is fully rendered and all reactive updates are processed before executing the code afterwards
  await nextTick();

  const appRoots = getAppRoots();
  if (!appRoots.length) {
    return;
  }

  // Clear any stale inert state before third-party scripts initialize
  clearInertState();

  inertObservers = appRoots.map(root => {
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "inert" && root.hasAttribute("inert")) {
          clearInertState();
        }
      }
    });
    observer.observe(root, { attributes: true });
    return observer;
  });

  // Install Pendo after the app is mounted to avoid blocking the initial render
  try {
    await installPendo();
  } finally {
    // Guarantee app interactivity after Pendo initialization path completes
    clearInertState();
  }
});

onUnmounted(() => {
  for (const observer of inertObservers) {
    observer.disconnect();
  }
  inertObservers = [];
});

//If inert attribute ever changes to true, reset it to undefined to ensure the app remains interactive
watch(isInert, newValue => {
  if (newValue) {
    clearInertState();
  }
});

watch(
  () => userStore.currentUser,
  async newUser => {
    //Only check for chatbot beta access if the chatbot feature flag is off and user is logged in, to avoid unnecessary API call
    if (newUser && !chatbotFlag.value) {
      try {
        const response = await unifiedApi.checkChatbotBetaAccess();
        hasChatBotBetaAccess.value = response.data;
      } catch (error) {
        logEvent("checkChatbotBetaAccessError", componentName, (error as Error).message);
      }
    }
  }
);

async function toggleAutoLogoutModal(type: string | null) {
  type = type || "modal";
  logEvent("toggleAutoLogoutModal", componentName, `${showAutoLogoutDialog.value ? "Hiding" : "Showing"} via ${type}`);
  showAutoLogoutDialog.value = !showAutoLogoutDialog.value;
}

async function continueUserSession() {
  await reloadSubscriberInfo();
  toggleAutoLogoutModal("close");
}

function logoutUser() {
  toggleAutoLogoutModal("close");
  logout();
}

// This will check if the user is logged in and his session is idle
// for more than XX minutes, then will logout the user
function checkIfIsStillLogged() {
  if (MAX_SESSION_IDLE_DURATION > 0) {
    window.setInterval(() => {
      if (userStore.currentUser) {
        const initialTime = userStore.lastRequestTime;
        const currentTime = new Date().getTime();
        if (initialTime > 0 && !showAutoLogoutDialog.value) {
          // If established Account, show modal after 30 min
          if (
            currentTime - initialTime > MAX_SESSION_IDLE_DURATION - AUTO_LOGOUT_INTERVAL_MSEC &&
            userStore.isEstablishedAccount
          ) {
            toggleAutoLogoutModal("open");
          } else if (
            // If preliminary Account, keep connection alive every 5 min up to 120 min.
            // TODO: Change interval to 29 min, after finding out on why API session is expiring in 10 min.
            currentTime - initialTime > PRELIMINARY_USER_KEEP_ALIVE_TIME_IN_MSEC &&
            !userStore.isEstablishedAccount
          ) {
            if (userStore.preliminaryKeepAliveTimeInMinutes < PRELIMINARY_USER_TOTAL_KEEP_ALIVE_TIME_IN_MINUTES) {
              //Refresh the session
              unifiedApi.getUser(true);
              //Add time to the keep alive timer
              userStore.preliminaryKeepAliveTimeInMinutes += PRELIMINARY_USER_KEEP_ALIVE_TIME_IN_MSEC / MSEC_IN_MINUTE;
              userStore.lastRequestTime = new Date().getTime();
            } else {
              //Reset the time back to 0
              userStore.preliminaryKeepAliveTimeInMinutes = 0;
              //Clear the current user but not the userStore email. Email is used to pre-populate the email input field
              userStore.currentUser = null;
              //If the user is on the order confirmation page
              if (route.name === "orderConfirmation") {
                notificationsStore.addNotification({
                  type: "PreliminaryUserOrderConfirmationTimeOut" as NotificationType,
                  dismissible: false,
                });
              } else {
                //If the user is still in the buy flow
                notificationsStore.addNotification({
                  type: "PreliminaryUserTimeOut" as NotificationType,
                  dismissible: false,
                });
              }
              userStore.preliminaryUserTimedOut = true;
              //Go to the email step and don't clear notifications
              changePageState(buyFlowPageStates.email, false);
              buyFlowManagerStore.setEditStep(layoutItemNames.email);
            }
          }
        }
      }
    }, SESSION_CHECK_INTERVAL);
  }
}
</script>
